import { useCallback, useMemo } from 'react';
import { getClipFPS, isNotFullLengthClip, isOriginalLayoutAvailable } from '@/libs/clipUtils';
import { Clip } from '@/domains/asset';
import { isSplitsInTimelineEnabled } from '@/stores/clip';
import {
  getAllCompositeSegments,
  getAllSegments
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/compositeUtils';
import { SegmentType } from '@/App/remotion/MainPlayer/types/Segment/Type';
import { updateClipData } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/constants';
import {
  getDeletesForSegment,
  getEditsForSegment,
  getScenesForSegment
} from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/playerConfigUtil';
import { isAudioContent } from '@/stores/core';
import { showErrorToast } from '@/libs/toast/toast';
import useTranscriptPage from '@/Pages/TranscriptPage/useTranscriptPage';

const useCreateClip = ({ clip }: { clip: Clip }) => {
  const { createClip } = useTranscriptPage();

  const isCreateClipAllowed = useMemo(() => {
    if (isNotFullLengthClip(clip)) {
      return false;
    }
    return true;
  }, [clip]);

  const getDefaultLayout = useCallback(() => {
    if (isAudioContent()) {
      return 'AUDIOGRAM';
    }
    if (isOriginalLayoutAvailable(clip)) {
      return 'DEFAULT';
    }
    return 'GRID';
  }, [clip]);

  const onCreateClip = useCallback(
    (startTime: number, endTime: number, startSegmentId?: string, endSegmentId?: string) => {
      const hasMultipleScenes = (clip.asset_metadata.scenes || []).length > 1;
      if (isSplitsInTimelineEnabled(clip) && startSegmentId && endSegmentId) {
        const additionalParams: Record<string, any> = {};
        const allSegments = getAllSegments(clip);

        const lastSegment = allSegments[allSegments.length - 1];
        const fps = getClipFPS(clip);
        const lastSegmentEndTime =
          lastSegment.type === SegmentType.COMPOSITE || lastSegment.type === SegmentType.VIDEO
            ? lastSegment.segment_metadata?.maxVideoDuration
            : lastSegment.toFrame / fps;

        if (startTime === null || endTime === null) {
          return Promise.reject(new Error('Start time or end time is null'));
        }

        // Check if there is at least one composite segment between startSegmentId and endSegmentId
        const startSegmentIndex = allSegments.findIndex(seg => seg.id === startSegmentId);
        const endSegmentIndex = allSegments.findIndex(seg => seg.id === endSegmentId);

        if (startSegmentIndex !== -1 && endSegmentIndex !== -1) {
          const segmentsBetween = allSegments.slice(startSegmentIndex, endSegmentIndex + 1);
          const hasComposite = segmentsBetween.some(seg => seg.type === SegmentType.COMPOSITE);

          if (!hasComposite) {
            showErrorToast('At least one recording segment is required between the selected start and end time.');
            return Promise.reject(new Error('At least one composite segment is required'));
          }
        }

        const clipDataWithStartTrim = updateClipData(
          {
            clipId: clip.id,
            actionType: CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE,
            startTime: endTime,
            startSegmentId: endSegmentId || '',
            endSegmentId: lastSegment.id || '',
            endTime: lastSegmentEndTime || 0,
            deleteType: 'MANUAL'
          },
          {
            currentClipData: updateClipData(
              {
                clipId: clip.id,
                actionType: CLIP_UPDATE_TYPE.ADD_COMPOSITE_SEGMENT_DELETE,
                startTime: 0,
                startSegmentId: allSegments[0].id,
                endSegmentId: startSegmentId || '',
                endTime: startTime,
                deleteType: 'MANUAL'
              },
              {
                skipStoreAndBackendUpdate: true
              }
            ),
            skipStoreAndBackendUpdate: true
          }
        ) as Clip;

        const clipDataWithEndAndStartTrim = updateClipData(
          {
            clipId: clip.id,
            actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_LAYOUT,
            layout: getDefaultLayout()
          },
          {
            currentClipData: clipDataWithStartTrim,
            skipStoreAndBackendUpdate: true
          }
        );
        additionalParams.player_config = clipDataWithEndAndStartTrim.asset_metadata.player_config;
        additionalParams.layout = hasMultipleScenes ? clip.asset_metadata.layout : getDefaultLayout();
        additionalParams.duration = clipDataWithEndAndStartTrim.asset_metadata.duration;

        const allSegmentAfterEndSegment = getAllCompositeSegments(clipDataWithEndAndStartTrim);
        const minStartTime = Math.min(...allSegmentAfterEndSegment.map(seg => seg.segment_metadata?.start || 0));
        const maxEndTime = Math.max(...allSegmentAfterEndSegment.map(seg => seg.segment_metadata?.end || 0));

        return createClip(minStartTime, maxEndTime, {
          edits: clip.asset_metadata.edits,
          deletes: clip.asset_metadata.deletes || {},
          scenes: clip.asset_metadata.scenes,
          fps,
          additionalParams
        });
      }
      const clipDataWithUpdatedLayout = hasMultipleScenes
        ? clip
        : updateClipData(
            {
              clipId: clip.id,
              actionType: CLIP_UPDATE_TYPE.UPDATE_CLIP_LAYOUT,
              layout: getDefaultLayout()
            },
            {
              skipStoreAndBackendUpdate: true
            }
          );
      return createClip(startTime, endTime, {
        edits: getEditsForSegment(clipDataWithUpdatedLayout),
        deletes: getDeletesForSegment(clipDataWithUpdatedLayout),
        scenes: getScenesForSegment(clipDataWithUpdatedLayout),
        fps: getClipFPS(clip),
        additionalParams: {
          layout: hasMultipleScenes ? clip.asset_metadata.layout : getDefaultLayout()
        }
      });
    },
    [clip, createClip, getDefaultLayout]
  );

  return { isCreateClipAllowed, onCreateClip };
};

export default useCreateClip;
